/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import NGOFirstRun from "./ngo/NGOFirstRun";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
// @ts-ignore
import tblLogo from '../tbl_logo.png';
import { useAuth } from '../contexts/AuthContext';

//==============================================================================
// TYPE DEFINITIONS
//==============================================================================

interface Metric {
    id: string;
    project_id: string;
    metric_name: string;
    target_value: number;
    current_value?: number;
    unit?: string;
}

interface FundRequest {
    id: string;
    project_id: string;
    amount: number;
    status: string;
}

//==============================================================================
// HELPER COMPONENTS
//==============================================================================

const ScoreCard: React.FC<{
    label: string;
    value: number | string;
    color?: string;
}> = ({ label, value, color = "#10b981" }) => (
    <div className="dashboard-scorecard">
        <div className="scorecard-content">
            <div className="scorecard-value" style={{ color }}>{value}</div>
            <div className="scorecard-label">{label}</div>
        </div>
    </div>
);

//==============================================================================
// MAIN DASHBOARD COMPONENT
//==============================================================================
const NGODashboard: React.FC = () => {
    const { userProfile } = useAuth();
    const ngoId = userProfile?.ngo_id;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showFirstRunModal, setShowFirstRunModal] = useState(false);

    const [projectDetails, setProjectDetails] = useState<any[]>([]);
    const [metrics, setMetrics] = useState<Metric[]>([]);
    const [fundRequests, setFundRequests] = useState<FundRequest[]>([]);
    const [fundDisbursements, setFundDisbursements] = useState<any[]>([]);
    const [projectExpenditures, setProjectExpenditures] = useState<any[]>([]);
    const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>('all');
    const [complianceSnapshot, setComplianceSnapshot] = useState<{ pending: number; done: number }>({ pending: 0, done: 0 });
    const [ngoName, setNgoName] = useState<string>('NGO');
    const [ngoLogoUrl, setNgoLogoUrl] = useState<string | null>(null);
    const [ngoDocs, setNgoDocs] = useState<any[]>([]);
    const [ngoUploads, setNgoUploads] = useState<any[]>([]);
    const [showAllNgoDocs, setShowAllNgoDocs] = useState(false);
    const [documentAssignments, setDocumentAssignments] = useState<any[]>([]);
    const dashboardRef = useRef<HTMLDivElement>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [backgroundImages, setBackgroundImages] = useState<string[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            if (!ngoId) {
                setError('NGO ID not found');
                setLoading(false);
                return;
            }

            try {
                // Fetch NGO data to check status
                const { data: ngoData, error: ngoError } = await supabase
                    .from('ngos')
                    .select('profile_complete, status, legal_name, logo_url, company_id')
                    .eq('id', ngoId)
                    .single();

                if (ngoError) throw ngoError;

                // Set NGO name and logo
                if (ngoData?.legal_name) {
                    setNgoName(ngoData.legal_name);
                }
                if (ngoData?.logo_url) {
                    let path = ngoData.logo_url;
                    if (path.includes('/object/public/ngo-documents/')) path = path.split('/object/public/ngo-documents/')[1].split('?')[0];
                    else if (path.includes('/ngo-documents/')) path = path.split('/ngo-documents/')[1].split('?')[0];
                    if (path) {
                        const { data: signedData } = await supabase.storage.from('ngo-documents').createSignedUrl(decodeURIComponent(path), 3600);
                        if (signedData?.signedUrl) setNgoLogoUrl(signedData.signedUrl);
                    }
                }

                const isVerified = ngoData?.status === 'verified';

                // If not verified, show first-run modal
                if (!isVerified) {
                    setShowFirstRunModal(true);
                    setLoading(false);
                    return;
                }

                // Fetch projects assigned to this NGO
                const { data: projectNgos, error: projectNgosError } = await supabase
                    .from('project_ngos')
                    .select(`
                        project_id,
                        status,
                        projects:project_id (
                            id,
                            name,
                            summary,
                            status,
                            thematic_area,
                            budget,
                            created_at,
                            company_id
                        )
                    `)
                    .eq('ngo_id', ngoId)
                    .in('status', ['accepted', 'pending', 'assigned', 'changes_requested', 'pre_start', 'active']);

                if (projectNgosError) throw projectNgosError;

                const safeProjects = (projectNgos || [])
                    .filter((pn: any) => pn.projects !== null && pn.projects !== undefined)
                    .map((pn: any) => pn.projects.id);

                const projectDetailsList = (projectNgos || [])
                    .filter((pn: any) => pn.projects !== null && pn.projects !== undefined)
                    .map((pn: any) => ({
                        ...pn.projects,
                        assignment_status: pn.status
                    }));

                setProjectDetails(projectDetailsList);

                const baseCompanyId = ngoData?.company_id;
                const projectCompanyIds = projectDetailsList.map(p => p.company_id).filter(Boolean);
                const companyIds = Array.from(new Set([...(baseCompanyId ? [baseCompanyId] : []), ...projectCompanyIds]));
                if (companyIds.length > 0) {
                    const { data: companiesData } = await supabase
                        .from('companies')
                        .select('dashboard_images')
                        .in('id', companyIds);
                    
                    let allImages: string[] = [];
                    companiesData?.forEach(c => {
                        if (c.dashboard_images && Array.isArray(c.dashboard_images)) {
                            allImages.push(...c.dashboard_images);
                        }
                    });

                    if (allImages.length > 0) {
                        const pathsToSign: { url: string, path: string }[] = [];

                        for (const rawUrl of allImages) {
                            let path = rawUrl;
                            if (path.includes('/object/public/company-documents/')) {
                                path = path.split('/object/public/company-documents/')[1].split('?')[0];
                            } else if (path.includes('/company-documents/')) {
                                path = path.split('/company-documents/')[1].split('?')[0];
                            } else if (path.startsWith('company-documents/')) {
                                path = path.substring('company-documents/'.length);
                            } else if (path.startsWith('http') || path.startsWith('/images/')) {
                                continue;
                            }
                            if (path) {
                                pathsToSign.push({ url: rawUrl, path: decodeURIComponent(path) });
                            }
                        }

                        const signedMap: Record<string, string> = {};
                        if (pathsToSign.length > 0) {
                            const { data: signedData } = await supabase.storage.from('company-documents').createSignedUrls(pathsToSign.map(p => p.path), 3600);
                            if (signedData) {
                                signedData.forEach((item, index) => {
                                    if (item.signedUrl) {
                                        signedMap[pathsToSign[index].url] = item.signedUrl;
                                    }
                                });
                            }
                        }

                        const finalImageUrls = allImages.map(url => signedMap[url] || url);
                        if (finalImageUrls.length > 0) {
                            setBackgroundImages(finalImageUrls);
                        }
                    }
                }

                const projectIds = safeProjects;

                if (projectIds.length > 0) {
                    // Fetch metrics, fund requests, fund disbursements, and project expenditures
                    const [metricsDefRes, metricsProgressRes, fundRequestsRes, disbursementsRes, expendituresRes] = await Promise.all([
                        supabase
                            .from('project_metrics')
                            .select('id, project_id, metric_name, target_value, unit')
                            .in('project_id', projectIds),
                        supabase
                            .from('project_metrics_progress')
                            .select('project_id, metric_name, current_value')
                            .in('project_id', projectIds),
                        supabase
                            .from('fund_requests')
                            .select('id, project_id, amount, status')
                            .eq('ngo_id', ngoId)
                            .in('status', ['pending', 'approved']),
                        supabase
                            .from('fund_disbursements')
                            .select('id, amount, project_id')
                            .eq('ngo_id', ngoId),
                        supabase
                            .from('project_expenditures')
                            .select('id, project_id, expenditure_amount, description')
                            .in('project_id', projectIds)
                    ]);

                    if (metricsDefRes.error) console.warn('Failed to fetch metrics:', metricsDefRes.error);
                    if (fundRequestsRes.error) console.warn('Failed to fetch fund requests:', fundRequestsRes.error);
                    if (disbursementsRes.error) console.warn('Failed to fetch fund disbursements:', disbursementsRes.error);
                    if (expendituresRes.error) console.warn('Failed to fetch project expenditures:', expendituresRes.error);

                    const progressMap: Record<string, number> = {};
                    if (!metricsProgressRes.error && metricsProgressRes.data) {
                        metricsProgressRes.data.forEach((p: any) => {
                            progressMap[`${p.project_id}_${p.metric_name}`] = p.current_value || 0;
                        });
                    }

                    setMetrics((metricsDefRes.data || []).map((m: any) => ({
                        id: m.id,
                        project_id: m.project_id,
                        metric_name: m.metric_name,
                        target_value: m.target_value || 0,
                        current_value: progressMap[`${m.project_id}_${m.metric_name}`] || 0,
                        unit: m.unit || ''
                    })));

                    setFundRequests(fundRequestsRes.data || []);
                    setFundDisbursements(disbursementsRes.data || []);
                    setProjectExpenditures(expendituresRes.data || []);
                }

                // Fetch document assignments for this NGO specifically
                const { data: assignmentsData } = await supabase
                    .from('document_assignments')
                    .select('*')
                    .eq('ngo_id', ngoId);

                const rawAssignments = assignmentsData || [];

                // Fetch global compliance repository for hybrid auto-assignments
                const { data: repoDocsData } = await supabase
                    .from('document_repository')
                    .select('*')
                    .eq('category', 'compliance')
                    .eq('is_active', true);

                const fullRepoDocs = repoDocsData || [];
                const augmentedAssignments = [...rawAssignments];

                fullRepoDocs.forEach((doc: any) => {
                    if (doc.target_type === 'ngo' || doc.role === 'ngo') {
                        if (!doc.visible_ngo_ids || (Array.isArray(doc.visible_ngo_ids) && doc.visible_ngo_ids.includes(ngoId))) {
                            if (!augmentedAssignments.find(a => a.document_repository_id === doc.id)) {
                                augmentedAssignments.push({
                                    id: `auto-ngo-${doc.id}`,
                                    document_repository_id: doc.id,
                                    ngo_id: ngoId,
                                    status: 'Pending',
                                    is_auto: true
                                });
                            }
                        }
                    }
                });

                // Filter out ghost assignments (where the document no longer exists)
                const cleanAssignments = augmentedAssignments.filter((a: any) =>
                    fullRepoDocs.some((rd: any) => rd.id === a.document_repository_id)
                );

                setDocumentAssignments(cleanAssignments);
                const assignmentDocIds = cleanAssignments.map((a: any) => a.document_repository_id);

                if (assignmentDocIds.length > 0) {
                    const docs = fullRepoDocs.filter((d: any) => assignmentDocIds.includes(d.id));
                    setNgoDocs(docs);

                    // Fetch uploads for this NGO
                    const { data: uploadedData } = await supabase
                        .from('document_uploads')
                        .select('*')
                        .eq('ngo_id', ngoId)
                        .eq('status', 'active');

                    setNgoUploads(uploadedData || []);

                    const totalRequiredDocs = cleanAssignments.length;

                    // A doc is "done" if there is an active upload for its assignment OR repo ID
                    const doneDocsCount = cleanAssignments.filter(a =>
                        (uploadedData || []).some((u: any) =>
                            u.assignment_id === a.id ||
                            u.document_repository_id === a.document_repository_id
                        )
                    ).length;

                    setComplianceSnapshot({ pending: Math.max(0, totalRequiredDocs - doneDocsCount), done: doneDocsCount });
                } else {
                    setNgoDocs([]);
                    setNgoUploads([]);
                    setComplianceSnapshot({ pending: 0, done: 0 });
                }

            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [ngoId]);

    const dashboardStats = useMemo(() => {
        // Only include active projects for dashboard metrics
        let filteredProjects = projectDetails.filter(p => p.status === 'active');
        if (selectedProjectFilter !== 'all') {
            filteredProjects = filteredProjects.filter(p => p.id === selectedProjectFilter);
        }

        const activeProjects = filteredProjects.filter(p => p.status === 'active').length;
        const completedProjects = filteredProjects.filter(p => p.status === 'completed' || p.status === 'closed').length;
        const allocatedBudget = filteredProjects.reduce((sum, p) => sum + (p.budget || 0), 0);

        // Calculate total utilised from fund_disbursements (funds released to NGO)
        // This matches the Company's view of "Utilised" for NGO projects
        const totalUtilised = fundDisbursements.reduce((sum, disb) => {
            if (selectedProjectFilter === 'all' || disb.project_id === selectedProjectFilter) {
                const amount = typeof disb.amount === 'string'
                    ? parseFloat(disb.amount)
                    : (disb.amount || 0);
                return sum + amount;
            }
            return sum;
        }, 0);

        // Filter metrics by selected project
        let filteredMetrics = metrics;
        if (selectedProjectFilter !== 'all') {
            filteredMetrics = filteredMetrics.filter(m => m.project_id === selectedProjectFilter);
        }
        const totalMetrics = filteredMetrics.length;
        const completedMetrics = filteredMetrics.filter(m => (m.current_value || 0) >= m.target_value).length;

        // Filter fund requests by selected project
        let filteredFundRequests = fundRequests;
        if (selectedProjectFilter !== 'all') {
            filteredFundRequests = filteredFundRequests.filter(fr => fr.project_id === selectedProjectFilter);
        }
        const pendingFundRequests = filteredFundRequests.filter(fr => fr.status === 'pending').length;
        const totalBudget = filteredFundRequests.reduce((sum, fr) => sum + (fr.amount || 0), 0);

        return {
            totalProjects: filteredProjects.length,
            activeProjects,
            completedProjects,
            allocatedBudget,
            totalUtilised,
            totalMetrics,
            completedMetrics,
            pendingFundRequests,
            totalBudget
        };
    }, [projectDetails, fundDisbursements, metrics, fundRequests, selectedProjectFilter, projectExpenditures]);

    const ngoDocsTableData = useMemo(() => {
        const data: any[] = [];

        // Filter assignments to only those linking to NGO role docs
        const myAssignments = documentAssignments.filter(a => {
            const doc = ngoDocs.find(d => d.id === a.document_repository_id);
            return !!doc;
        });

        myAssignments.forEach(assignment => {
            const doc = ngoDocs.find(d => d.id === assignment.document_repository_id);
            if (!doc) return;

            // Match upload by assignment_id or fallback to legacy mapping
            const upload = ngoUploads.find(u => u.assignment_id === assignment.id)
                || ngoUploads.find(u => u.ngo_id === ngoId && u.document_repository_id === doc.id);

            data.push({
                id: assignment.id,
                ngoName: ngoName,
                documentName: doc.document_name,
                status: upload
                    ? (['Yes', 'Completed'].includes(upload.compliance_status || '') ? 'Completed' :
                        (['No', 'In Progress'].includes(upload.compliance_status || '')) ? 'In Progress' :
                            upload.compliance_status === 'Pending' ? 'Pending' : 'Completed')
                    : 'Pending',
                remarks: upload ? (upload.notes || upload.admin_notes || (['No', 'In Progress'].includes(upload.compliance_status || '') ? 'In Progress' : 'Approved & Adopted')) : '-',
                rawExpiry: (upload && upload.expiry_date) ? upload.expiry_date : (assignment.due_date || doc.due_date),
                validity: (upload && upload.expiry_date) ? `Expires: ${new Date(upload.expiry_date).toLocaleDateString('en-IN')}` :
                    (assignment.due_date || doc.due_date ? `Due: ${new Date(assignment.due_date || doc.due_date).toLocaleDateString('en-IN')}` : '-'),
                fileUrl: upload ? upload.file_url : null
            });
        });
        return data.sort((a, b) => {
            const aDaysLeft = a.rawExpiry ? Math.ceil((new Date(a.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24)) : 999;
            const bDaysLeft = b.rawExpiry ? Math.ceil((new Date(b.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24)) : 999;

            const aIsExpiring = aDaysLeft <= 15;
            const bIsExpiring = bDaysLeft <= 15;

            if (aIsExpiring && !bIsExpiring) return -1;
            if (!aIsExpiring && bIsExpiring) return 1;

            return 0; // Maintain order
        });
    }, [ngoDocs, ngoUploads, ngoName, documentAssignments, ngoId]);



    const ngoComplianceVisualData = useMemo(() => {
        const total = documentAssignments.length;
        // Count assignments by status
        const uploadedCount = documentAssignments.filter(a =>
            ngoUploads.some(u =>
                ((u.assignment_id === a.id) || (u.document_repository_id === a.document_repository_id && u.ngo_id === ngoId)) &&
                ['Yes', 'Completed'].includes(u.compliance_status || '')
            )
        ).length;

        const inProgressCount = documentAssignments.filter(a =>
            ngoUploads.some(u =>
                ((u.assignment_id === a.id) || (u.document_repository_id === a.document_repository_id && u.ngo_id === ngoId)) &&
                ['No', 'In Progress'].includes(u.compliance_status || '')
            )
        ).length;

        const pendingCount = Math.max(0, total - uploadedCount - inProgressCount);
        const percent = total > 0 ? (uploadedCount / total) * 100 : 0;

        let statusColor = '#ef4444';
        if (percent > 30 && percent <= 70) {
            statusColor = '#facc15';
        } else if (percent > 70) {
            statusColor = '#10b981';
        }

        if (total === 0) return [];

        return [{
            id: ngoId,
            name: 'All Compliance',
            total,
            uploadedCount,
            inProgressCount,
            pendingCount,
            percent,
            statusColor,
            chartData: [
                { name: 'Completed', value: uploadedCount, color: '#10b981' },
                { name: 'In Progress', value: inProgressCount, color: '#facc15' },
                { name: 'Pending', value: pendingCount, color: '#cbd5e1' }
            ]
        }];
    }, [ngoId, documentAssignments, ngoUploads]);

    const budgetVisualData = useMemo(() => {
        const total = dashboardStats.allocatedBudget;
        const utilised = dashboardStats.totalUtilised;
        const remaining = Math.max(0, total - utilised);
        const percent = total > 0 ? (utilised / total) * 100 : 0;

        // Match compliance color logic: Red (<30%), Yellow (30-70%), Green (>70%)
        let statusColor = '#ef4444';
        if (percent > 30 && percent <= 70) {
            statusColor = '#facc15';
        } else if (percent > 70) {
            statusColor = '#10b981';
        }

        return {
            total,
            utilised,
            remaining,
            percent,
            statusColor,
            chartData: total > 0
                ? [
                    { name: 'Utilised', value: utilised, color: statusColor },
                    { name: 'Remaining', value: remaining, color: '#e2e8f0' } // Slightly lighter grey for background
                ]
                : [
                    { name: 'Utilised', value: 0, color: '#e2e8f0' },
                    { name: 'Remaining', value: 1, color: '#e2e8f0' } // Full grey if no budget
                ]
        };
    }, [dashboardStats]);

    const projectBudgetVsUtilisedData = useMemo(() => {
        const projectMap: Record<string, { name: string; budget: number; utilised: number }> = {};
        projectDetails.filter(p => p.status === 'active').forEach(project => {
            if (selectedProjectFilter === 'all' || project.id === selectedProjectFilter) {
                projectMap[project.id] = {
                    name: project.name.length > 25 ? project.name.substring(0, 25) + '...' : project.name,
                    budget: project.budget || 0,
                    utilised: 0
                };
            }
        });

        fundDisbursements.forEach(disb => {
            if (projectMap[disb.project_id]) {
                projectMap[disb.project_id].utilised += parseFloat(disb.amount as string) || 0;
            }
        });

        return Object.values(projectMap).map(project => ({
            name: project.name,
            Budget: project.budget,
            Utilised: project.utilised
        }));
    }, [projectDetails, projectExpenditures, selectedProjectFilter]);


    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatShortINR = (value: number) => {
        if (!value || value === 0) return '₹0';
        if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
        if (value >= 100000) return `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 1)}L`;
        if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
        return `₹${value}`;
    };

    const CustomYAxisTick = (props: any) => {
        const { x, y, payload } = props;
        const text = payload.value || '';
        const words = text.split(' ');

        let line1 = '';
        let line2 = '';

        for (let i = 0; i < words.length; i++) {
            if ((line1 + ' ' + words[i]).trim().length <= 18 || i === 0) {
                line1 = (line1 + ' ' + words[i]).trim();
            } else {
                line2 = words.slice(i).join(' ');
                break;
            }
        }

        if (line2.length > 18) {
            line2 = line2.substring(0, 16) + '...';
        }

        return (
            <g transform={`translate(${x},${y})`}>
                {line2 ? (
                    <>
                        <text x={-6} y={-4} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
                            {line1}
                        </text>
                        <text x={-6} y={8} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
                            {line2}
                        </text>
                    </>
                ) : (
                    <text x={-6} y={3} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
                        {line1}
                    </text>
                )}
            </g>
        );
    };

    const handleDownloadPDF = async () => {
        if (!dashboardRef.current) return;
        setIsExporting(true);

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pdfWidth - (2 * margin);

            const container = dashboardRef.current;
            const sections = container.querySelectorAll('.pdf-section');
            const headerElement = container.querySelector('.pdf-header') as HTMLElement;

            // Capture Header once
            let headerImgData = '';
            let headerHeight = 0;
            if (headerElement) {
                const headerCanvas = await html2canvas(headerElement, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#f5f1f0',
                    windowWidth: 1400
                });
                headerImgData = headerCanvas.toDataURL('image/png');
                headerHeight = (headerCanvas.height * contentWidth) / headerCanvas.width;
            }

            const addHeaderAndFooter = (pagePdf: jsPDF, pageNum: number) => {
                // Header Logos
                if (headerImgData) {
                    pagePdf.addImage(headerImgData, 'PNG', margin, 5, contentWidth, headerHeight);
                }

                // Timestamp
                pagePdf.setFontSize(8);
                pagePdf.setTextColor(150);
                pagePdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, headerHeight + 10);

                // Footer
                pagePdf.setFontSize(8);
                pagePdf.setTextColor(150);
                pagePdf.text("Anchored and Developed By TBL Consultancy Services", pdfWidth - 85, pdfHeight - 10);
                pagePdf.text(`Page ${pageNum}`, margin, pdfHeight - 10);
            };

            let currentY = headerHeight + 20; // Start below header
            let currentPage = 1;
            addHeaderAndFooter(pdf, currentPage);

            for (let i = 0; i < sections.length; i++) {
                const section = sections[i] as HTMLElement;

                const canvas = await html2canvas(section, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#f5f1f0',
                    windowWidth: 1400,
                    onclone: (clonedDoc) => {
                        const btn = clonedDoc.querySelector('.btn-outline');
                        if (btn) (btn as HTMLElement).style.display = 'none';
                    }
                });

                const imgData = canvas.toDataURL('image/png');
                const imgProps = pdf.getImageProperties(imgData);
                const imgHeight = (imgProps.height * contentWidth) / imgProps.width;

                if (currentY + imgHeight > pdfHeight - 15) {
                    pdf.addPage();
                    currentPage++;
                    currentY = 25;
                    addHeaderAndFooter(pdf, currentPage);
                }

                pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, imgHeight);
                currentY += imgHeight + 5;
            }

            pdf.save(`${ngoName}_Dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsExporting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <>
                {showFirstRunModal && <NGOFirstRun />}
                <div className="dashboard-error">
                    <h3>Dashboard Could Not Be Loaded</h3>
                    <p>{error}</p>
                </div>
            </>
        );
    }

    return (
        <>
            {showFirstRunModal && <NGOFirstRun />}
            <div className="ngo-dashboard" id="ngo-dashboard-container" ref={dashboardRef} style={{ backgroundColor: 'var(--bg)', minHeight: '100vh', padding: '1.25rem 2rem 3rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
                {/* PDF-only Header (Off-screen but rendered for pre-loading) */}
                <div className="pdf-header" style={{
                    position: 'absolute',
                    top: '-10000px',
                    left: '0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px 0',
                    marginBottom: '30px',
                    borderBottom: '2px solid #e2e8f0',
                    width: '100%',
                    backgroundColor: '#f5f1f0'
                }}>
                    <div style={{ width: '250px', display: 'flex', justifyContent: 'flex-start' }}>
                        <img
                            src={ngoLogoUrl || ''}
                            alt="NGO Logo"
                            style={{ height: '60px', objectFit: 'contain' }}
                            onError={(e: any) => e.target.style.display = 'none'}
                        />
                    </div>
                    <div style={{ width: '250px', display: 'flex', justifyContent: 'flex-end' }}>
                        <img src={tblLogo} alt="TBL Logo" style={{ height: '60px', objectFit: 'contain' }} />
                    </div>
                </div>
                {/* Welcome Section - Center Aligned */}
                {/* Welcome Section - Premium Scrolling Background Sync */}
                <div className="pdf-section ngo-hero-section">
                    {/* Export Button */}
                    <button
                        onClick={handleDownloadPDF}
                        disabled={isExporting}
                        className="btn btn-outline ngo-export-btn"
                        style={{
                            backgroundColor: isExporting ? 'rgba(241, 245, 249, 0.9)' : 'rgba(255,255,255,0.9)',
                            color: isExporting ? '#64748b' : '#0f172a',
                            cursor: isExporting ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                        </svg>
                        {isExporting ? 'Preparing PDF...' : 'Download PDF'}
                    </button>
                    {/* Scrolling Background Wrapper */}
                    <div className="scrolling-bg-container" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        height: '100%',
                        zIndex: 0,
                    }}>
                        {(backgroundImages.length > 0 ? [...backgroundImages, ...backgroundImages] : [1, 2, 3, 4, 1, 2, 3, 4]).map((item, i) => (
                            <img
                                key={i}
                                src={typeof item === 'number' ? `/images/dashboard/${item}.jpg` : item}
                                className="scrolling-bg-image"
                                alt=""
                                style={{ height: '100%', width: '450px', objectFit: 'cover', objectPosition: 'center center' }}
                            />
                        ))}
                    </div>

                    {/* Contrast Overlay */}
                    <div className="ngo-hero-overlay" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.45))',
                        zIndex: 1
                    }} />

                    {ngoLogoUrl && (
                        <div className="ngo-hero-logo">
                            <img src={ngoLogoUrl} alt={`${ngoName} logo`}
                                style={{ height: '40px', maxWidth: '160px', objectFit: 'contain' }}
                            />
                        </div>
                    )}
                    <h1 className="ngo-hero-title">
                        Welcome, {ngoName}
                    </h1>
                    <p className="ngo-hero-subtitle">
                        Track your project progress, manage compliance, and make a meaningful impact in your community.
                    </p>
                </div>

                {/* Filters */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>Filter by Project</label>
                        <select
                            value={selectedProjectFilter}
                            onChange={(e) => setSelectedProjectFilter(e.target.value)}
                            style={{
                                padding: '0.5rem 1rem',
                                border: '1px solid var(--border-light)',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                background: 'white',
                                cursor: 'pointer',
                                minWidth: '200px'
                            }}
                        >
                            <option value="all">All Projects</option>
                            {projectDetails.filter(p => p.status === 'active').map(project => (
                                <option key={project.id} value={project.id}>{project.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Scorecards Row */}
                <div className="pdf-section" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '1.5rem',
                    marginBottom: '2.5rem'
                }}>
                    <ScoreCard
                        label="Total Projects"
                        value={dashboardStats.totalProjects}
                        color="#10b981"
                    />
                    <ScoreCard
                        label="Allocated Budget to Projects"
                        value={formatCurrency(dashboardStats.allocatedBudget)}
                        color="#10b981"
                    />
                    <ScoreCard
                        label="Total Utilised Till Date"
                        value={formatCurrency(dashboardStats.totalUtilised)}
                        color="#10b981"
                    />
                </div>



                {/* Financial Overview - Budget vs Utilisation */}
                <div className="card pdf-section" style={{ padding: '2rem', marginBottom: '2rem' }}>
                    <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', marginBottom: '1.5rem' }}>Total Budget vs Utilised (%)</h2>
                    <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                        <div style={{ textAlign: 'center', width: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            {/* Logo or name above gauge */}
                            <div style={{ marginBottom: '0.5rem', minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {ngoLogoUrl ? (
                                    <img src={ngoLogoUrl} alt={`${ngoName} logo`}
                                        style={{ height: '36px', maxWidth: '120px', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: 0, textAlign: 'center' }}>
                                        {ngoName.length > 18 ? ngoName.substring(0, 18) + '...' : ngoName}
                                    </h4>
                                )}
                            </div>

                            <div style={{ position: 'relative', height: '160px', width: '220px', overflow: 'hidden' }}>
                                <ResponsiveContainer width="100%" height={240}>
                                    <PieChart>
                                        {/* Background Track */}
                                        <Pie
                                            data={[{ value: 1 }]}
                                            cx="50%"
                                            cy="55%"
                                            startAngle={180}
                                            endAngle={0}
                                            innerRadius={80}
                                            outerRadius={105}
                                            cornerRadius={10}
                                            dataKey="value"
                                            stroke="none"
                                            fill="#cbd5e1"
                                        />
                                        {/* Utilised Progress Layer */}
                                        <Pie
                                            data={[{ value: budgetVisualData.utilised / (budgetVisualData.total || 1) }]}
                                            cx="50%"
                                            cy="55%"
                                            startAngle={180}
                                            endAngle={180 - (budgetVisualData.utilised / (budgetVisualData.total || 1)) * 180}
                                            innerRadius={80}
                                            outerRadius={105}
                                            cornerRadius={10}
                                            dataKey="value"
                                            stroke="none"
                                            fill="#10b981"
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{ position: 'absolute', top: '75%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                    <span style={{ fontSize: '2rem', fontWeight: 400, color: '#10b981' }}>{budgetVisualData.percent.toFixed(2)}%</span>
                                </div>
                            </div>
                            <div style={{ marginTop: '0' }}>
                                <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)' }}>{formatCurrency(budgetVisualData.utilised)}</div>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Utilised till Date</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* NGO Compliance Visuals */}
                {ngoComplianceVisualData.length > 0 && (
                <div className="card pdf-section" style={{ padding: '2rem', marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Compliance Completion (%)</h2>
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', fontWeight: 600, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                                <span style={{ color: 'var(--text-secondary)' }}>Completed</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#facc15' }}></div>
                                <span style={{ color: 'var(--text-secondary)' }}>In Progress</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#cbd5e1' }}></div>
                                <span style={{ color: 'var(--text-secondary)' }}>Pending</span>
                            </div>
                        </div>
                    </div>
                        <div style={{ display: 'flex', gap: '3rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                            {ngoComplianceVisualData.map(ngoData => (
                                <div key={ngoData.id} style={{ textAlign: 'center', width: '220px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {/* Logo or name above gauge */}
                                    <div style={{ marginBottom: '0.5rem', minHeight: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {ngoLogoUrl ? (
                                            <img src={ngoLogoUrl} alt={`${ngoName} logo`}
                                                style={{ height: '36px', maxWidth: '120px', objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', margin: 0, textAlign: 'center' }}>
                                                {ngoName.length > 18 ? ngoName.substring(0, 18) + '...' : ngoName}
                                            </h4>
                                        )}
                                    </div>
                                    <div style={{ position: 'relative', height: '160px', width: '220px', overflow: 'hidden' }}>
                                        <ResponsiveContainer width="100%" height={240}>
                                            <PieChart>
                                                {/* Background Track */}
                                                <Pie
                                                    data={[{ value: 1 }]}
                                                    cx="50%"
                                                    cy="55%"
                                                    startAngle={180}
                                                    endAngle={0}
                                                    innerRadius={80}
                                                    outerRadius={105}
                                                    cornerRadius={10}
                                                    dataKey="value"
                                                    stroke="none"
                                                    fill="#cbd5e1"
                                                />
                                                {/* In Progress + Completed Layer */}
                                                <Pie
                                                    data={[{ value: (ngoData.uploadedCount + ngoData.inProgressCount) / (ngoData.total || 1) }]}
                                                    cx="50%"
                                                    cy="55%"
                                                    startAngle={180}
                                                    endAngle={180 - ((ngoData.uploadedCount + ngoData.inProgressCount) / (ngoData.total || 1)) * 180}
                                                    innerRadius={80}
                                                    outerRadius={105}
                                                    cornerRadius={10}
                                                    dataKey="value"
                                                    stroke="none"
                                                    fill="#facc15"
                                                />
                                                {/* Completed Layer */}
                                                <Pie
                                                    data={[{ value: ngoData.uploadedCount / (ngoData.total || 1) }]}
                                                    cx="50%"
                                                    cy="55%"
                                                    startAngle={180}
                                                    endAngle={180 - (ngoData.uploadedCount / (ngoData.total || 1)) * 180}
                                                    innerRadius={80}
                                                    outerRadius={105}
                                                    cornerRadius={10}
                                                    dataKey="value"
                                                    stroke="none"
                                                    fill="#10b981"
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '75%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                            <span style={{ fontSize: '2rem', fontWeight: 400, color: '#10b981' }}>{ngoData.percent.toFixed(2)}%</span>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '0' }}>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text)' }}>
                                            {ngoData.uploadedCount}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                            Completed Documents
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                                            {ngoData.inProgressCount} In Progress | {ngoData.pendingCount} Pending
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div >
                )}

                {/* NGO My Documents Table */}
                <div className="card pdf-section" style={{ marginBottom: '2rem', overflow: 'hidden' }}>
                    <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid var(--border-light)', backgroundColor: '#f5f1f0' }}>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>CSR Compliance: My Documents</h2>
                    </div>

                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', padding: '1rem' }}>
                        <table style={{ width: '100%', minWidth: '550px', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'var(--primary-dark)' }}>
                                    <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Document</th>
                                    <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Status</th>
                                    <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Remarks</th>
                                    <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Validity</th>
                                    <th style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 700, color: '#fff', borderBottom: '1px solid var(--border-light)' }}>Link</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(showAllNgoDocs ? ngoDocsTableData : ngoDocsTableData.slice(0, 4)).map(row => {
                                    let isExpired = false;
                                    let isExpiringSoon = false;
                                    if (row.rawExpiry) {
                                        const daysLeft = Math.ceil((new Date(row.rawExpiry).getTime() - Date.now()) / (1000 * 3600 * 24));
                                        if (daysLeft <= 0) isExpired = true;
                                        else if (daysLeft <= 15) isExpiringSoon = true;
                                    }
                                    const isHighlight = isExpired || isExpiringSoon;
                                    const bgColor = isHighlight ? '#fee2e2' : 'white';
                                    const textColor = isHighlight ? '#991b1b' : 'var(--text)';

                                    return (
                                        <tr key={row.id} style={{ borderBottom: '1px solid var(--border-light)', backgroundColor: bgColor }}>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                                                {row.documentName}
                                                {isExpired && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#ef4444', color: 'white', borderRadius: '4px' }}>Expired</span>}
                                                {isExpiringSoon && <span style={{ marginLeft: '8px', padding: '2px 6px', fontSize: '0.7rem', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px' }}>Expires Soon</span>}
                                            </td>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.status}</td>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>{row.remarks}</td>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor, fontWeight: isHighlight ? 700 : 400 }}>{row.validity}</td>
                                            <td style={{ padding: '1rem', fontSize: '0.875rem', color: textColor }}>
                                                {row.fileUrl ? (
                                                    <button onClick={() => window.open(row.fileUrl, '_blank')} className="btn btn-primary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', backgroundColor: isHighlight ? '#b91c1c' : '' }}>View</button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    )
                                })}
                                {ngoDocsTableData.length === 0 && (
                                    <tr>
                                        <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No documents found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {ngoDocsTableData.length > 4 && (
                            <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid var(--border-light)' }}>
                                <button
                                    onClick={() => setShowAllNgoDocs(!showAllNgoDocs)}
                                    className="btn btn-outline"
                                    style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '20px' }}
                                >
                                    {showAllNgoDocs ? 'See Less' : `See More (${ngoDocsTableData.length - 4} more)`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>



                {/* Charts */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '4rem', width: '100%' }}>
                    {projectBudgetVsUtilisedData.length > 0 && (
                        <div className="card pdf-section" style={{ padding: '1.25rem 1rem' }}>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text)' }}>
                                Project-wise Budget vs. Utilised
                            </h3>
                            <div style={{ width: '100%' }}>
                                <ResponsiveContainer width="100%" height={Math.max(260, projectBudgetVsUtilisedData.length * 55 + 70)}>
                                    <BarChart data={projectBudgetVsUtilisedData} layout="vertical" margin={{ top: 10, right: 15, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" tickFormatter={formatShortINR} tick={{ fontSize: 10, fill: '#64748b' }} />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            width={125}
                                            tick={<CustomYAxisTick />}
                                            interval={0}
                                        />
                                        <Tooltip formatter={(value: number) => formatCurrency(value)} />
                                        <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: '8px' }} />
                                        <Bar dataKey="Budget" fill="#fbbf24" radius={[0, 4, 4, 0]} barSize={14} />
                                        <Bar dataKey="Utilised" fill="#10b981" radius={[0, 4, 4, 0]} barSize={14} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Metrics Scorecards */}
                    <div className="card" style={{ padding: '2rem 1.5rem', marginBottom: '2rem' }}>
                        <div className="pdf-section" style={{ marginBottom: '1.5rem' }}>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
                                Project-wise Impact Metrics
                            </h3>
                        </div>

                        <div>
                            {(() => {
                                const activeProjectIds = projectDetails.filter(p => p.status === 'active').map(p => p.id);
                                const filteredMetrics = metrics.filter(m =>
                                    activeProjectIds.includes(m.project_id) &&
                                    (selectedProjectFilter === 'all' || m.project_id === selectedProjectFilter)
                                );

                                if (filteredMetrics.length === 0) {
                                    return (
                                        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                            No metrics found.
                                        </div>
                                    );
                                }

                                const groupedMetrics = filteredMetrics.reduce((acc, metric) => {
                                    const project = projectDetails.find(p => p.id === metric.project_id);
                                    const projectName = project?.name || 'General';
                                    if (!acc[projectName]) acc[projectName] = [];
                                    acc[projectName].push(metric);
                                    return acc;
                                }, {} as Record<string, typeof metrics>);

                                return Object.entries(groupedMetrics).map(([projectName, projectMetrics]) => (
                                    <div key={projectName} className="pdf-section" style={{ marginBottom: '3rem' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            marginBottom: '1.25rem',
                                            paddingBottom: '0.5rem',
                                            borderBottom: '1px solid var(--border-light)'
                                        }}>
                                            <div style={{
                                                width: '4px',
                                                height: '18px',
                                                backgroundColor: '#10b981',
                                                borderRadius: '2px'
                                            }}></div>
                                            <h4 style={{
                                                fontSize: '1rem',
                                                fontWeight: 700,
                                                color: 'var(--text)',
                                                margin: 0
                                            }}>
                                                {projectName}
                                            </h4>
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                            gap: '1.5rem',
                                            justifyItems: 'center'
                                        }}>
                                            {projectMetrics.map((metric) => {
                                                const achieved = metric.current_value || 0;
                                                const target = metric.target_value || 0;
                                                const progress = target > 0 ? (achieved / target) * 100 : 0;

                                                let color = '#9ca3af';
                                                if (target > 0) {
                                                    if (progress >= 80) color = '#10b981';
                                                    else if (progress >= 30) color = '#f59e0b';
                                                    else color = '#ef4444';
                                                }

                                                const displayProgress = Math.min(progress, 100);
                                                const radius = 75;
                                                const stroke = 8;
                                                const normalizedRadius = radius - stroke * 2;
                                                const circumference = normalizedRadius * 2 * Math.PI;
                                                const strokeDashoffset = target > 0 ? circumference - (displayProgress / 100) * circumference : circumference;

                                                return (
                                                    <div key={metric.id} style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        textAlign: 'center',
                                                        width: '100%',
                                                        maxWidth: '260px'
                                                    }}>
                                                        <h5 style={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: 600,
                                                            marginBottom: '0.75rem',
                                                            color: 'var(--text)',
                                                            minHeight: '2.2rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            lineHeight: 1.3
                                                        }}>
                                                            {metric.metric_name}
                                                        </h5>

                                                        <div style={{ position: 'relative', width: radius * 2, height: radius * 2 }}>
                                                            <svg height={radius * 2} width={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
                                                                <circle
                                                                    stroke="#f3f4f6"
                                                                    fill="transparent"
                                                                    strokeWidth={stroke}
                                                                    r={normalizedRadius}
                                                                    cx={radius}
                                                                    cy={radius}
                                                                />
                                                                <circle
                                                                    stroke={color}
                                                                    fill="transparent"
                                                                    strokeWidth={stroke}
                                                                    strokeDasharray={circumference + ' ' + circumference}
                                                                    style={{ strokeDashoffset, transition: 'stroke-dashoffset 1s ease-in-out' }}
                                                                    strokeLinecap="round"
                                                                    r={normalizedRadius}
                                                                    cx={radius}
                                                                    cy={radius}
                                                                />
                                                            </svg>

                                                            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                                <span style={{ fontSize: '1.6rem', fontWeight: 700, color, lineHeight: 1 }}>
                                                                    {target > 0 ? `${progress.toFixed(0)}%` : '--'}
                                                                </span>
                                                                <span style={{ fontSize: '1.15rem', fontWeight: 600, color: 'var(--text)', marginTop: '0.2rem', lineHeight: 1 }}>
                                                                    {achieved.toLocaleString('en-IN')}
                                                                </span>
                                                                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem', fontWeight: 500 }}>
                                                                    of {target > 0 ? target.toLocaleString('en-IN') : '--'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>

                </div>

                {/* Styles */}
                <style>{`
                    .ngo-dashboard {
                        padding: 0.25rem 1rem 3rem 1rem;
                        max-width: 1400px;
                        margin: 0 auto;
                        background: var(--bg);
                    }

                    .dashboard-scorecards {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                        gap: 1.5rem;
                        margin-bottom: 2rem;
                    }

                    .dashboard-scorecard {
                        background: white;
                        border-radius: 12px;
                        padding: 1.5rem;
                        box-shadow: var(--shadow);
                        border: 1px solid var(--border-light);
                        transition: transform 0.2s, box-shadow 0.2s;
                    }

                    .dashboard-scorecard:hover {
                        transform: translateY(-2px);
                        box-shadow: var(--shadow-md);
                    }

                    .scorecard-content {
                        width: 100%;
                    }

                    .scorecard-value {
                        font-size: 1.75rem;
                        font-weight: 700;
                        margin-bottom: 0.25rem;
                    }

                    .scorecard-label {
                        font-size: 0.875rem;
                        color: var(--text-secondary);
                        font-weight: 500;
                    }

                    .dashboard-error {
                        text-align: center;
                        padding: 4rem 2rem;
                    }

                    .dashboard-error h3 {
                        color: var(--danger);
                        margin-bottom: 0.5rem;
                    }

                    .dashboard-error p {
                        color: var(--text-secondary);
                    }

                    .spinner {
                        width: 40px;
                        height: 40px;
                        border: 4px solid var(--border-light);
                        border-top: 4px solid var(--primary);
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .overview-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                        gap: 1.5rem;
                    }

                    .quick-link-card {
                        background: white;
                        border-radius: 12px;
                        padding: 1.5rem;
                        box-shadow: var(--shadow);
                        border: 1px solid var(--border-light);
                        transition: transform 0.2s, box-shadow 0.2s;
                        text-decoration: none;
                        color: inherit;
                        display: block;
                    }

                    .quick-link-card:hover {
                        transform: translateY(-2px);
                        box-shadow: var(--shadow-md);
                        text-decoration: none;
                    }

                    .quick-link-title {
                        font-size: 1.125rem;
                        font-weight: 600;
                        color: var(--text);
                        margin-bottom: 0.5rem;
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }

                    .quick-link-description {
                        font-size: 0.875rem;
                        color: var(--text-secondary);
                        margin: 0;
                    }

                    @media (max-width: 768px) {
                        .ngo-dashboard {
                            padding: 0.25rem 0.5rem 3rem 0.5rem;
                        }

                        .dashboard-scorecards {
                            grid-template-columns: repeat(2, 1fr);
                            gap: 1rem;
                        }

                        .overview-grid {
                            grid-template-columns: 1fr;
                        }
                    }
                `}</style>
            </div >
        </>
    );
};

export default NGODashboard;
